import React, { useState, useEffect } from "react";
import { Box, Button, TextField, Typography, styled, InputAdornment, IconButton } from "@mui/material";
import ContentUsBgImage from "@assets/ContentUsBgImage.png";
import { redirectToOidcSignup, signupWithEmail } from "@services/auth";
import { tryGetSinglePaymentPendingFacilityForPricing } from "@utils/verifiedFacilityRedirect";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { getSsoButtonSettings, SsoButtonsSettings } from "../../services/ssoSettings";
import validator from "validator";
import { isDomainBlocked } from "@utils/emailValidation";
import { parseAuthErrorMessage } from "@utils/ssoErrorMessages";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  authPanelTaglineForVertical,
  landingHomePathForVertical,
  landingVerticalFromSearch,
} from "@utils/appPath";
import { syncPreviewVerticalSession } from "@services/verticals";
import { savePendingSignupVerification } from "@utils/signupVerification";
const SignUpContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffffff",
  //width: "100%",
  //minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  padding: "16px",
  // overflowX: "hidden",
  [theme.breakpoints.down("sm")]: {
    padding: "8px",
    alignItems: "flex-start",
    paddingTop: "20px",
  },
}));

const ContactSection = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "32px",
  position: "relative",
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  gap: 24,
  [theme.breakpoints.down("lg")]: {
    gap: 16,
    flexDirection: "column",
    padding: "24px 16px",
    maxWidth: "900px",
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    padding: "20px 12px",
    gap: 12,
    maxWidth: "100%",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "16px 8px",
    gap: 8,
  },
}));

const BackgroundOverlay = styled(Box)({
  position: "absolute",
  height: "100%",
  left: 0,
  top: 0,
  width: "100%",
  backgroundImage: `
    radial-gradient(ellipse 42.02px 68.593px at 298.8px 124.35px, rgba(220,253,255,0.4) 0%, rgba(255,255,255,0) 100%),
    radial-gradient(ellipse 77.28px 6.0674px at 1471.2px 363.44px, rgba(220,253,255,0.4) 0%, rgba(255,255,255,0) 100%),
    radial-gradient(ellipse 1.62px 57.671px at 682.2px 879.48px, rgba(197,255,225,0.4) 0%, rgba(255,255,255,0) 100%)
  `,
});
const GradientBackground = styled(Box)({
  position: "absolute",
  inset: 0,
  background:
    "radial-gradient(ellipse at 138px 40px, #065b70 0%, #0a829d 100%)",
  opacity: 0.9,
  backgroundImage: `url(${ContentUsBgImage})`,
  width: "100%",
  height: "100%",
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
});
const LeftPanel = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "620px", // match Login LeftPanel width
  height: "700px", // match Login LeftPanel height
  backgroundImage: `url(${ContentUsBgImage})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  borderRadius: "32px",
  border: "1px solid #ffffff",
  overflow: "hidden",
  marginRight: "-80px",
  flexShrink: 0,
  [theme.breakpoints.down("lg")]: {
    width: "480px",
    height: "520", // match Login lg height
    marginRight: "-20px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "500px",
    height: "auto",
    minHeight: "320px", // match Login md minHeight
    aspectRatio: "16 / 10",
    marginRight: 0,
    borderRadius: "24px",
  },
  [theme.breakpoints.down("sm")]: {
    maxWidth: "100%",
    minHeight: "240px",
    borderRadius: "16px",
  },
}));

const LeftPanelContent = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: "95.5px",
  top: "101px",
  width: "544px",
  color: "#ffffff",
  [theme.breakpoints.down("lg")]: {
    left: "32px",
    top: "48px",
    width: "380px",
  },
  [theme.breakpoints.down("md")]: {
    position: "relative",
    left: 0,
    top: 0,
    width: "100%",
    padding: "24px",
    textAlign: "center",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "18px 0px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "64px",
  lineHeight: "1.1",
  marginBottom: "120px",
  [theme.breakpoints.down("lg")]: {
    fontSize: "42px",
    marginBottom: "40px",
  },
  [theme.breakpoints.down("md")]: {
    fontSize: "36px",
    marginBottom: "16px",
    lineHeight: "1.2",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "28px",
    marginBottom: "12px",
    paddingTop: "48px",
  },
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "18px",
  lineHeight: "1.5",
  width: "422px",
  whiteSpace: "normal",
  overflowWrap: "normal",
  wordBreak: "normal",
  [theme.breakpoints.down("lg")]: {
    width: "360px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    fontSize: "16px",
    lineHeight: "1.45",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
    lineHeight: "1.4",
    maxWidth: 320,
    margin: "0 auto",
    textAlign: "center",
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  backgroundColor: "#f4feff",
  border: "1px solid #05495a",
  borderRadius: "16px",
  padding: "28px 24px 28px 28px",
  width: "480px",
  position: "relative",
  flexShrink: 0,
  [theme.breakpoints.down("lg")]: {
    width: "420px",
    padding: "24px 20px 24px 24px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "480px",
    padding: "20px 16px 20px 20px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "16px 12px 16px 16px",
    borderRadius: "12px",
    margin: "0 6px",
    maxWidth: "90%",
  },
}));

const CloseButton = styled(Button)(({ theme }) => ({
  position: "absolute",
  right: "47px",
  top: "47px",
  minWidth: "28px",
  width: "28px",
  height: "28px",
  padding: 0,
  color: "#033843",
  "&:hover": {
    backgroundColor: "rgba(3, 56, 67, 0.04)",
  },
  [theme.breakpoints.down("sm")]: {
    right: "12px",
    top: "12px",
  },
}));

const FormContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  width: "100%",
});

const PageTitle = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 700,
  fontSize: "28px",
  lineHeight: "35px",
  color: "#033843",
  height: "35px",
});

const SocialButtonsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "12px",
  alignItems: "flex-start",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    gap: "10px",
    width: "100%",
  },
}));

const SocialButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(180deg, #ffffff 0%, #f6feff 100%)",
  border: "1px solid #bfe5ec",
  borderRadius: "64px",
  height: "40px",
  padding: "4px 33px",
  display: "flex",
  alignItems: "center",
  gap: "10px",
  boxShadow:
    "0px 1px 2px -1px rgba(0,0,0,0.1), 0px 1px 3px 0px rgba(0,0,0,0.1)",
  textTransform: "none",
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "14px",
  letterSpacing: "-0.4px",
  color: "#065b70",
  minHeight: "44px",
  "&:hover": {
    backgroundColor: "#f0f9fa",
    boxShadow:
      "0px 2px 4px -1px rgba(0,0,0,0.1), 0px 2px 6px 0px rgba(0,0,0,0.1)",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    justifyContent: "center",
    fontSize: "16px",
    height: "48px",
    minHeight: "48px",
    padding: "12px 16px",
  },
}));

const OrDivider = styled(Box)({
  padding: "6px",
  display: "flex",
  alignItems: "center",
  gap: "6px",
  width: "100%",
});

const DividerLine = styled(Box)({
  height: "1px",
  backgroundColor: "#e0e0e0",
  flex: 1,
});

const OrText = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "20px",
  color: "#262626",
});

const InputContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4.5px",
  width: "100%",
});

const InputLabel = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "20px",
  color: "#262626",
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    height: "42px",
    borderRadius: "4px",
    "& fieldset": {
      borderColor: "#e6fafc",
    },
    "&:hover fieldset": {
      borderColor: "#bfe5ec",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0a829d",
    },
    boxShadow:
      "inset 0px 0px 8px 0px rgba(0,108,116,0.12), inset 0px 0px 4px 0px rgba(34,110,127,0.25)",
  },
  "& .MuiOutlinedInput-input": {
    padding: "11px 14px",
    fontSize: "14px",
    fontFamily: "Rethink Sans, sans-serif",
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
      padding: "12px 14px",
    },
  },
  [theme.breakpoints.down("sm")]: {
    "& .MuiOutlinedInput-root": {
      height: "48px",
      minHeight: "44px",
    },
  },
}));

const SignUpButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(90deg, #0a829d 0%, #35d7e4 100%)",
  border: "1px solid #ffffff",
  borderRadius: "60px",
  height: "60px",
  width: "100%",
  padding: "12px 24px",
  boxShadow:
    "0px 4px 24px 0px rgba(10,130,157,0.2), 0px 4px 7.3px 0px rgba(133,233,255,0.4)",
  textTransform: "none",
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "18px",
  lineHeight: "22px",
  color: "#ffffff",
  minHeight: "44px",
  "&:hover": {
    background: "linear-gradient(90deg, #086e86 0%, #2bc9d6 100%)",
  },
  [theme.breakpoints.down("sm")]: {
    height: "52px",
    minHeight: "48px",
    fontSize: "16px",
    borderRadius: "48px",
  },
}));

const TermsContainer = styled(Box)(({ theme }) => ({
  padding: "8px",
  display: "flex",
  flexDirection: "column",
  gap: "4.5px",
  alignItems: "center",
  width: "100%",
  [theme.breakpoints.down("sm")]: {
    textAlign: "center",
    gap: "8px",
  },
}));

const TermsText = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "5px",
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "20px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "16px",
    lineHeight: "24px",
    flexWrap: "wrap",
    justifyContent: "center",
  },
}));

const TermsLink = styled("span")(({ theme }) => ({
  color: "#0a829d",
  textDecoration: "underline",
  cursor: "pointer",
  padding: "4px 8px",
  borderRadius: "4px",
  "&:hover": {
    color: "#086e86",
    backgroundColor: "rgba(10, 130, 157, 0.05)",
  },
  [theme.breakpoints.down("sm")]: {
    display: "inline-block",
    minHeight: "44px",
    lineHeight: "36px",
    padding: "4px 12px",
  },
}));

const SignInContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "4.5px",
  width: "100%",
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "20px",
  minHeight: "44px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "16px",
    lineHeight: "24px",
    flexWrap: "wrap",
    textAlign: "center",
  },
}));

const SignInLink = styled("span")(({ theme }) => ({
  color: "#0a829d",
  textDecoration: "underline",
  cursor: "pointer",
  padding: "8px",
  borderRadius: "4px",
  "&:hover": {
    color: "#086e86",
    backgroundColor: "rgba(10, 130, 157, 0.05)",
  },
  [theme.breakpoints.down("sm")]: {
    display: "inline-block",
    minHeight: "44px",
    lineHeight: "28px",
  },
}));

export default function Signup() {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const emailQueryParam = searchParams.get("email") ?? "";
  const initialEmailFromUrl =
    emailQueryParam && validator.isEmail(emailQueryParam)
      ? emailQueryParam.trim()
      : "";

  const [email, setEmail] = useState(initialEmailFromUrl);
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [displayName] = useState("");
  const [loading, setLoading] = useState(false);
  const [ssoBusy, setSsoBusy] = useState(false);
  const ssoInFlightRef = React.useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [prefilledFromInputForm, setPrefilledFromInputForm] = useState(false);
  const [emailFromUrl] = useState(!!initialEmailFromUrl);
  const [emailDomainError, setEmailDomainError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showSsoButtons, setShowSsoButtons] = useState(true);
  const [autofillBgColor, setAutofillBgColor] = useState("transparent");
  const passwordInputRef = React.useRef<HTMLInputElement>(null);
  const authBusy = loading || ssoBusy;
  const navigate = useNavigate();
  const landingVertical = landingVerticalFromSearch(location.search);
  const panelTagline = authPanelTaglineForVertical(landingVertical);
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let isMounted = true;
    getSsoButtonSettings()
      .then((settings: SsoButtonsSettings) => {
        if (isMounted) {
          setShowSsoButtons(settings.signup_sso !== false);
        }
      })
      .catch(() => {
        if (isMounted) {
          setShowSsoButtons(true);
        }
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // Detect autofill background color on password field
  useEffect(() => {
    const checkAutofill = () => {
      if (passwordInputRef.current) {
        const input = passwordInputRef.current;
        const computedStyle = window.getComputedStyle(input);
        const bgColor = computedStyle.backgroundColor;
        setAutofillBgColor(bgColor);
      }
    };

    // Check on mount and periodically (autofill happens after render)
    checkAutofill();
    const timer = setTimeout(checkAutofill, 100);
    const interval = setInterval(checkAutofill, 500);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [password, showPassword]);

  // Prefill from SignupInputForm (firstName, lastName, email); first/last name remain editable, email read-only
  useEffect(() => {
    try {
      const state = location.state as any;
      let prefill: any = state && state.fromSignupInputForm ? state : null;

      if (prefill) {
        if (prefill.firstName) setFirstName(prefill.firstName);
        if (prefill.lastName) setLastName(prefill.lastName);
        if (prefill.email) setEmail(prefill.email);
        setPrefilledFromInputForm(true);
      }
    } catch {
      // Ignore prefill errors
    }
  }, [location.state]);

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setError(null);
    setEmailDomainError(null);

    if (value.trim() && validator.isEmail(value)) {
      if (isDomainBlocked(value)) {
        setEmailDomainError(
          "Please use your work email address. Personal email domains are not supported."
        );
      }
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setEmailDomainError(null);

    // Basic email validation
    if (!email.trim()) {
      setError("Email is required");
      return;
    }
    if (!validator.isEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }
    if (isDomainBlocked(email)) {
      setEmailDomainError(
        "Please use your work email address. Personal email domains are not supported."
      );
      return;
    }
    setLoading(true);
    try {
      const response = await signupWithEmail({
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        display_name: displayName || `${firstName} ${lastName}`.trim(),
      });
      // Redirect: if verified and has one preferred facility in payment-pending state -> pricing; else dashboard with setup popup or email-verification
      const isVerified = response?.user?.is_verified === true;
      if (isVerified && response?.access_token) {
        const paymentPendingFacility = await tryGetSinglePaymentPendingFacilityForPricing(
          landingVertical,
        );
        if (paymentPendingFacility) {
          const facilityTypeId =
            (paymentPendingFacility.facility_details as any)?.facility_type_id ?? null;
          navigate("/dashboard/facilities", {
            replace: true,
            state: {
              currentView: "pricing",
              fromPreferredFacilityId: paymentPendingFacility.id,
              facility_type_id: facilityTypeId,
              pricingReloadToken: Date.now(),
            },
          });
          return;
        }
        navigate("/dashboard", { replace: true, state: { showMyFacilityDialog: true } });
      } else if (isVerified) {
        navigate("/dashboard", { replace: true, state: { showMyFacilityDialog: true } });
      } else {
        savePendingSignupVerification({
          email,
          fromSignup: true,
          vertical: landingVertical,
        });
        navigate("/email-verification", {
          replace: true,
          state: { email, fromSignup: true, vertical: landingVertical },
        });
      }
    } catch (err: any) {
      let errorMessage = "Signup failed";
      try {
        if (err.message && err.message.startsWith('{')) {
          const parsed = JSON.parse(err.message);
          errorMessage = parsed?.detail || parsed?.message || "Signup failed";
        } else {
          errorMessage = err.message || "Signup failed";
        }
      } catch {
        errorMessage = err.message || "Signup failed";
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSocialSignUp = async (provider: "Google" | "Microsoft") => {
    // Ignore re-entry while email or SSO is already in flight (double-click).
    if (loading || ssoInFlightRef.current) {
      return;
    }
    ssoInFlightRef.current = true;
    setSsoBusy(true);
    setError(null);
    try {
      if (landingVertical) {
        syncPreviewVerticalSession(landingVertical);
      }
      const fromState = (location.state as { signupId?: string } | null)?.signupId;
      const signupId = String(
        searchParams.get("signupId") ||
          searchParams.get("signup_id") ||
          fromState ||
          "",
      ).trim();
      const socialDisplayName = `${firstName} ${lastName}`.trim();
      await redirectToOidcSignup(
        provider === "Google" ? "google" : "microsoft",
        signupId || undefined,
        socialDisplayName ? { displayName: socialDisplayName } : undefined,
      );
      // Keep busy until browser navigates to the IdP; do not clear on success.
    } catch (err: unknown) {
      ssoInFlightRef.current = false;
      setError(parseAuthErrorMessage(err, `${provider} sign-up failed`));
      setSsoBusy(false);
    }
  };

  const handleClose = () => {
    navigate(landingHomePathForVertical(landingVertical));
  };

  const handleSignInClick = () => {
    navigate(
      landingVertical ? `/login?vertical=${landingVertical}` : "/login",
    );
  };

  const handleTermsClick = () => {
    navigate("/terms-of-service");
  };

  const handlePrivacyClick = () => {
    navigate("/privacy-policy");
  };

  return (
    <SignUpContainer>
      <ContactSection>
        <BackgroundOverlay />

        <LeftPanel>
          <GradientBackground></GradientBackground>
          <LeftPanelContent>
            <MainTitle>Create Your UniComply Account</MainTitle>
            <SubTitle>{panelTagline}</SubTitle>
          </LeftPanelContent>
        </LeftPanel>

        <RightPanel>
          <CloseButton onClick={handleClose}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="29"
              height="29"
              viewBox="0 0 29 29"
              fill="none"
            >
              <g clipPath="url(#clip0_358_243)">
                <path
                  d="M14.5 0.25C6.76806 0.25 0.5 6.51806 0.5 14.25C0.5 21.9824 6.76806 28.25 14.5 28.25C22.2324 28.25 28.5 21.9824 28.5 14.25C28.5 6.51806 22.2324 0.25 14.5 0.25ZM14.5 26.5276C7.74544 26.5276 2.25 21.0046 2.25 14.2499C2.25 7.49538 7.74544 1.99995 14.5 1.99995C21.2546 1.99995 26.75 7.49541 26.75 14.2499C26.75 21.0045 21.2546 26.5276 14.5 26.5276ZM19.4494 9.30056C19.1077 8.95887 18.5539 8.95887 18.2122 9.30056L14.5 13.0128L10.7878 9.30056C10.4461 8.95887 9.89225 8.95887 9.55013 9.30056C9.20844 9.64225 9.20844 10.1961 9.55013 10.5378L13.2623 14.25L9.55013 17.9622C9.20844 18.3034 9.20844 18.8582 9.55013 19.1995C9.89181 19.5412 10.4457 19.5412 10.7878 19.1995L14.5 15.4873L18.2122 19.1995C18.5539 19.5412 19.1077 19.5412 19.4494 19.1995C19.7911 18.8582 19.7911 18.3034 19.4494 17.9622L15.7372 14.25L19.4494 10.5378C19.7916 10.1957 19.7916 9.64181 19.4494 9.30056Z"
                  fill="#033843"
                />
              </g>
              <defs>
                <clipPath id="clip0_358_243">
                  <rect
                    width="28"
                    height="28"
                    fill="white"
                    transform="translate(0.5 0.25)"
                  />
                </clipPath>
              </defs>
            </svg>
          </CloseButton>

          <FormContainer>
            <PageTitle>Sign Up</PageTitle>

            {showSsoButtons && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "18px" }}>
              <SocialButtonsContainer>
                <SocialButton
                  type="button"
                  disabled={authBusy}
                  aria-busy={ssoBusy}
                  onClick={() => handleSocialSignUp("Google")}
                >
                  <Box
                    component="span"
                    sx={{
                      width: "16px",
                      height: "16px",
                      backgroundImage:
                        "url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE1LjY4IDguMTgxODJIMTVWOEg4VjEwSDEyLjI5MDlDMTEuNzk4MiAxMS4zOTY0IDEwLjY1IDEyLjUgOS4yNSAxM0M3IDEzIDUuMTUgMTEuMTUgNS4xNSA4Ljg1QzUuMTUgNi41NSA3IDQuNjUgOS4yNSA0LjY1QzEwLjI5MTggNC42NSAxMS4yNjM2IDUuMDU0NTUgMTIgNS43NEwxMy40IDE0LjJDMTIuNiA0LjggMTAuNiAzIDggM0M0LjcgMyAyIDUuNyAyIDlTNC43IDE1IDggMTVTMTQgMTIuMyAxNCA5QzE0IDguNzI3MjcgMTMuODkwOSA4LjQ0NTQ1IDEzLjY4IDguMTgxODJaIiBmaWxsPSIjNEI1NTYzIi8+Cjwvc3ZnPgo=')",
                      backgroundSize: "contain",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "center",
                    }}
                  />
                  {ssoBusy ? "Signing you in…" : "Continue with Google"}
                </SocialButton>

                <SocialButton
                  type="button"
                  disabled={authBusy}
                  aria-busy={ssoBusy}
                  onClick={() => handleSocialSignUp("Microsoft")}
                >
                  <Box
                    component="span"
                    sx={{
                      width: "16px",
                      height: "16px",
                      backgroundImage:
                        "url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3QgeD0iMSIgeT0iMSIgd2lkdGg9IjYuNSIgaGVpZ2h0PSI2LjUiIGZpbGw9IiNGMjUwMjIiLz4KPHJlY3QgeD0iOC41IiB5PSIxIiB3aWR0aD0iNi41IiBoZWlnaHQ9IjYuNSIgZmlsbD0iIzc5QkE0MiIvPgo8cmVjdCB4PSIxIiB5PSI4LjUiIHdpZHRoPSI2LjUiIGhlaWdodD0iNi41IiBmaWxsPSIjMDA5OURGIi8+CjxyZWN0IHg9IjguNSIgeT0iOC41IiB3aWR0aD0iNi41IiBoZWlnaHQ9IjYuNSIgZmlsbD0iI0ZGQjkwMCIvPgo8L3N2Zz4K')",
                      backgroundSize: "contain",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "center",
                    }}
                  />
                  {ssoBusy ? "Signing you in…" : "Continue with Microsoft"}
                </SocialButton>
              </SocialButtonsContainer>
              {ssoBusy && (
                <Typography
                  role="status"
                  aria-live="polite"
                  variant="body2"
                  sx={{ color: "#065b70", textAlign: "center" }}
                  data-testid="signup-sso-busy"
                >
                  Signing you in…
                </Typography>
              )}

              <OrDivider>
                <DividerLine />
                <OrText>OR</OrText>
                <DividerLine />
              </OrDivider>
            </Box>
            )}

            <Box
                component="form"
                onSubmit={onSubmit}
                sx={{ display: "flex", flexDirection: "column", gap: "24px" }}
              >
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: "16px",
                    }}
                  >
                    <InputContainer>
                      <InputLabel>
                        First name <span style={{ color: "#dc2626" }}>*</span>
                      </InputLabel>
                      <StyledTextField
                        fullWidth
                        variant="outlined"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="Enter your first name"
                        required
                      />
                    </InputContainer>

                    <InputContainer>
                      <InputLabel>
                        Last name <span style={{ color: "#dc2626" }}>*</span>
                      </InputLabel>
                      <StyledTextField
                        fullWidth
                        variant="outlined"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Enter your last name"
                        required
                      />
                    </InputContainer>
                  </Box>

                  {/* <InputContainer>
                    <InputLabel>Display name</InputLabel>
                    <StyledTextField
                      fullWidth
                      variant="outlined"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="How should we address you?"
                    />
                  </InputContainer> */}

                  <InputContainer>
                    <InputLabel>
                      Email <span style={{ color: "#dc2626" }}>*</span>
                    </InputLabel>
                    <Typography
                      sx={{
                        fontFamily: "Rethink Sans, sans-serif",
                        fontWeight: 400,
                        fontSize: "12px",
                        lineHeight: "16px",
                        color: "#0a829d",
                        marginBottom: "6px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <InfoOutlined
                        fontSize="small"
                        sx={{ color: "#0a829d" }}
                      />
                      Use your work email to verify your organization
                    </Typography>
                    <StyledTextField
                      fullWidth
                      type="email"
                      variant="outlined"
                      value={email}
                      onChange={(e) => handleEmailChange(e.target.value)}
                      placeholder="Enter your email"
                      required
                      autoComplete="off"
                      disabled={prefilledFromInputForm || emailFromUrl}
                      InputProps={{ readOnly: prefilledFromInputForm || emailFromUrl }}
                      error={!!emailDomainError || error === "Email is required" || error === "Please enter a valid email address"}
                    />
                  </InputContainer>

                  <InputContainer>
                    <InputLabel>
                      Password <span style={{ color: "#dc2626" }}>*</span>
                    </InputLabel>
                    <StyledTextField
                      fullWidth
                      type={showPassword ? "text" : "password"}
                      variant="outlined"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      autoComplete="new-password"
                      inputRef={passwordInputRef}
                      InputProps={{
                        style: { backgroundColor: autofillBgColor },
                        endAdornment: (
                          <InputAdornment 
                            position="end" 
                            sx={{ 
                              backgroundColor: autofillBgColor,
                              height: '100%',
                              margin: 0,
                              padding: 0,
                              paddingRight: 0,
                              marginRight: '-14px',
                            }}
                          >
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              disableRipple
                              sx={{
                                color: "#666",
                                padding: "12px 14px",
                                margin: 0,
                                backgroundColor: `${autofillBgColor} !important`,
                                borderTopRightRadius: '8px',
                                borderBottomRightRadius: '8px',
                                borderTopLeftRadius: 0,
                                borderBottomLeftRadius: 0,
                                "&:hover": {
                                  backgroundColor: `${autofillBgColor} !important`,
                                },
                                "&:focus": {
                                  backgroundColor: `${autofillBgColor} !important`,
                                },
                                "&:active": {
                                  backgroundColor: `${autofillBgColor} !important`,
                                },
                              }}
                            >
                              {showPassword ? (
                                <VisibilityOff sx={{ fontSize: 20 }} />
                              ) : (
                                <Visibility sx={{ fontSize: 20 }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: autofillBgColor,
                          overflow: 'hidden',
                          "& fieldset": {
                            borderColor: "#bfe5ec",
                          },
                          // Hide browser's default password reveal button
                          "& input::-ms-reveal": {
                            display: "none",
                          },
                          "& input::-ms-clear": {
                            display: "none",
                          },
                        },
                        // Ensure only our icon shows
                        "& input[type='password']::-webkit-credentials-auto-fill-button": {
                          display: "none",
                        },
                      }}
                    />
                  </InputContainer>
                </Box>

                {emailDomainError && (
                  <Typography color="error" variant="body2">
                    {emailDomainError}
                  </Typography>
                )}

                {error && (
                  <Typography color="error" variant="body2">
                    {error}
                  </Typography>
                )}

                <SignUpButton type="submit" disabled={authBusy || !!emailDomainError}>
                  {loading ? "Signing up..." : ssoBusy ? "Signing you in…" : "SIGN UP"}
                </SignUpButton>

                <TermsContainer>
                  <TermsText>
                    <span style={{ color: "#737373" }}>
                      By continuing, you agree to{" "}
                    </span>
                    <TermsLink onClick={handleTermsClick}>
                      UniComply.ai's Terms of Service
                    </TermsLink>
                  </TermsText>
                  <TermsText>
                    <span style={{ color: "#737373" }}>and </span>
                    <TermsLink onClick={handlePrivacyClick}>
                      Privacy Policy
                    </TermsLink>
                  </TermsText>
                </TermsContainer>
              </Box>

            <SignInContainer>
              <span style={{ color: "#737373" }}>
                Already have an account?{" "}
              </span>
              <SignInLink onClick={handleSignInClick}>Sign in</SignInLink>
            </SignInContainer>
          </FormContainer>
        </RightPanel>
      </ContactSection>
    </SignUpContainer>
  );
}
